import React from 'react';
import PropTypes from 'prop-types';
import { PivotData } from './Utilities';

// Foreign brokers (red text)
const FOREIGN_BROKERS = [
  "AG", "AH", "AI", "AK", "BK", "BQ", "CG", "CS", "DP", "DR", "DU", "FS", "GW", "HD", "KK",
  "KZ", "LH", "LG", "LS", "MS", "RB", "RX", "TX", "YP", "YU", "ZP"
];

// Government brokers (green text)
const GOVERNMENT_BROKERS = ['CC', 'NI', 'OD', 'DX'];

// Helper function for price color
const getPriceColorInfo = (priceVal: any, prevCloseRaw: any): { color?: string; className: string } | undefined => {
  if (prevCloseRaw === null || prevCloseRaw === undefined) return undefined;
  
  const prevClose = parseFloat(String(prevCloseRaw).replace(/,/g, ''));
  const currentPrice = parseFloat(String(priceVal).replace(/,/g, ''));
  
  if (isNaN(prevClose) || isNaN(currentPrice)) return undefined;
  
  if (currentPrice > prevClose) return { color: '#10B981', className: 'font-bold' }; // Green
  if (currentPrice < prevClose) return { color: '#EF4444', className: 'font-bold' }; // Red
  return { color: '#EAB308', className: 'font-bold' }; // Yellow for equal (prev day close)
};

// Helper function to get broker color class based on type (for text color)
const getBrokerColorClass = (brokerCode: string): { color?: string; className: string } => {
  if (GOVERNMENT_BROKERS.includes(brokerCode)) {
    return { color: '#10B981', className: 'font-bold' }; // Green-600
  }
  if (FOREIGN_BROKERS.includes(brokerCode)) {
    return { color: '#EF4444', className: 'font-bold' }; // Red-600
  }
  return { className: 'font-bold' }; // Default color fallback with bold
};

// Helper function to set fixed column widths for row attributes to prevent resizing
const getColWidthStyle = (attr: string): React.CSSProperties => {
  if (attr === 'BO No' || attr === 'SO No') {
    return { minWidth: '60px', maxWidth: '60px', width: '60px', boxSizing: 'border-box' };
  }
  if (attr === 'TIME') {
    return { minWidth: '55px', maxWidth: '55px', width: '55px', boxSizing: 'border-box' };
  }
  if (attr === 'PRICE') {
    return { minWidth: '45px', maxWidth: '45px', width: '45px', boxSizing: 'border-box' };
  }
  if (attr === 'BUYER' || attr === 'SELLER') {
    return { minWidth: '60px', maxWidth: '60px', width: '60px', boxSizing: 'border-box' };
  }
  return {};
};

// helper function for setting row/col-span in pivotTableRenderer
const spanSize = function (arr: any[][], i: number, j: number) {
    let x;
    if (i !== 0) {
        let asc, end;
        let noDraw = true;
        for (
            x = 0, end = j, asc = end >= 0;
            asc ? x <= end : x >= end;
            asc ? x++ : x--
        ) {
            const rowPrev = arr[i - 1];
            const rowCurr = arr[i];
            if (rowPrev && rowCurr && rowPrev[x] !== rowCurr[x]) {
                noDraw = false;
            }
        }
        if (noDraw) {
            return -1;
        }
    }
    let len = 0;
    while (i + len < arr.length) {
        let asc1, end1;
        let stop = false;
        for (
            x = 0, end1 = j, asc1 = end1 >= 0;
            asc1 ? x <= end1 : x >= end1;
            asc1 ? x++ : x--
        ) {
            const rowCurr = arr[i];
            const rowNext = arr[i + len];
            if (rowCurr && rowNext && rowCurr[x] !== rowNext[x]) {
                stop = true;
            }
        }
        if (stop) {
            break;
        }
        len++;
    }
    return len;
};

function redColorScaleGenerator(values: number[]) {
    const min = Math.min.apply(Math, values);
    const max = Math.max.apply(Math, values);
    return function (x: number) {
        // eslint-disable-next-line no-magic-numbers
        const nonRed = 255 - Math.round((255 * (x - min)) / (max - min));
        return { backgroundColor: 'rgb(255,' + nonRed + ',' + nonRed + ')' };
    };
}

function makeRenderer(opts: any = {}) {
    class TableRenderer extends React.PureComponent<any, { displayLimit: number, collapsedRows: Record<string, boolean>, showSubtotals: boolean }> {
        static defaultProps = PivotData.defaultProps;
        static propTypes = PivotData.propTypes;

        observer: IntersectionObserver | null = null;
        sentinelRef: React.RefObject<HTMLDivElement> = React.createRef();
        containerRef: React.RefObject<HTMLDivElement> = React.createRef();
        _rowKeysLength: number = 0;

        pivotDataInstance: any = null;
        prevPivotProps: any = null;
        prevSortDirs: any = null;
        prevSortByOrderNumber: any = null;
        hakiCache: Record<string, boolean> = {};
        hakaCache: Record<string, boolean> = {};
        cachedSortedRowKeys: any[] = [];
        cachedVisibleKeyStrings: string[] = [];

        constructor(props: any) {
            super(props);
            this.state = {
                displayLimit: 200,
                expandedRows: {}
            };
        }

        toggleRow = (rKey: string) => {
            this.setState((s: any) => {
                const newExpanded = { ...(s.expandedRows || {}), [rKey]: !(s.expandedRows || {})[rKey] };
                if (this.props['tableOptions']?.onExpandedRowsChange) {
                    const hasExpanded = Object.values(newExpanded).some(v => v);
                    this.props['tableOptions'].onExpandedRowsChange(hasExpanded);
                }
                return { expandedRows: newExpanded };
            });
        };

        override componentDidMount() {
            this.attachScrollListener();
            window.addEventListener('collapseAllRows', this.handleCollapseAll);
        }

        override componentDidUpdate() {
            this.attachScrollListener();
        }

        override componentWillUnmount() {
            const el = this.containerRef.current;
            if (el) {
                el.removeEventListener('scroll', this.onNativeScroll);
            }
            window.removeEventListener('collapseAllRows', this.handleCollapseAll);
        }

        handleCollapseAll = () => {
            this.setState({ expandedRows: {} });
            if (this.props['tableOptions']?.onExpandedRowsChange) {
                this.props['tableOptions'].onExpandedRowsChange(false);
            }
        };

        attachScrollListener() {
            const el = this.containerRef.current;
            if (el) {
                el.removeEventListener('scroll', this.onNativeScroll);
                el.addEventListener('scroll', this.onNativeScroll, { passive: true });
            }
        }

        onNativeScroll = () => {
            const el = this.containerRef.current;
            if (!el) return;
            
            // If scrolled within 800px of the bottom, load more
            const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
            if (distanceFromBottom < 800 && this.state.displayLimit < this._rowKeysLength) {
                this.setState((s) => ({
                    displayLimit: s.displayLimit + 500
                }));
            }
        };

        override render() {
            const internalProps = { ...this.props };
            let injectedHakaHaki = false;
            // Secretly inject to avoid aggregating different sides
            if (!internalProps['rows']?.includes('HAKA HAKI') && !internalProps['cols']?.includes('HAKA HAKI')) {
                internalProps['rows'] = [...(internalProps['rows'] || []), 'HAKA HAKI'];
                injectedHakaHaki = true;
            }

            const sortDirs = (this.props['tableOptions'] || {}).sortDirs || {};
            const sortByOrderNumber = (this.props['tableOptions'] || {}).sortByOrderNumber;

            // Compare props to see if pivot data and sorting need to be recalculated
            const propsToCompare = [
                'data', 'rows', 'cols', 'vals', 'valueFilter', 
                'aggregatorName', 'rowOrder', 'colOrder',
                'derivedAttributes'
            ];

            let changed = !this.pivotDataInstance || !this.prevPivotProps;
            if (!changed) {
                if (this.prevSortDirs !== sortDirs || this.prevSortByOrderNumber !== sortByOrderNumber) {
                    changed = true;
                } else {
                    for (const p of propsToCompare) {
                        if (this.props[p] !== this.prevPivotProps[p]) {
                            changed = true;
                            break;
                        }
                    }
                }
            }

            if (changed) {
                this.pivotDataInstance = new PivotData(internalProps);
                this.prevPivotProps = this.props;
                this.prevSortDirs = sortDirs;
                this.prevSortByOrderNumber = sortByOrderNumber;

                const pivotData = this.pivotDataInstance;
                const fullRowAttrs = pivotData.props.rows;
                const rowAttrs = injectedHakaHaki ? fullRowAttrs.slice(0, -1) : fullRowAttrs;

                // 1. Rebuild HAKA HAKI cache
                this.hakiCache = {};
                this.hakaCache = {};
                if (fullRowAttrs.includes('BUYER') || fullRowAttrs.includes('SELLER')) {
                    const volCache: Record<string, { HAKA: number, HAKI: number }> = {};
                    
                    PivotData.forEachRecord(
                        pivotData.props.data,
                        pivotData.props.derivedAttributes,
                        (record: any) => {
                            if (pivotData.filter(record)) {
                                const rKey = fullRowAttrs.map((a: string) => 
                                    String(record[a] !== undefined && record[a] !== null ? record[a] : 'null')
                                ).join('\0');
                                
                                if (!volCache[rKey]) volCache[rKey] = { HAKA: 0, HAKI: 0 };
                                
                                const vol = parseFloat(String(record['VOLUME'] || '0')) || 1; 
                                
                                if (record['HAKA HAKI'] === 'HAKI') {
                                    volCache[rKey].HAKI += vol;
                                } else if (record['HAKA HAKI'] === 'HAKA') {
                                    volCache[rKey].HAKA += vol;
                                }
                            }
                        }
                    );
                    
                    for (const rKey in volCache) {
                        const stats = volCache[rKey];
                        if (!stats) continue;
                        if (stats.HAKI > stats.HAKA) {
                            this.hakiCache[rKey] = true;
                        } else if (stats.HAKA > stats.HAKI) {
                            this.hakaCache[rKey] = true;
                        } else if (stats.HAKI > 0) {
                            this.hakiCache[rKey] = true;
                        }
                    }
                }

                // 2. Sort Row Keys using Schwartzian Transform optimization
                const rowKeys = pivotData.getRowKeys();
                const hasAnySort = Object.values(sortDirs).some(d => d !== 'none');
                
                if (sortByOrderNumber || hasAnySort) {
                    const mergedData = internalProps['data'] || [];
                    
                    const mergedDataLookup = new Map<string, any>();
                    for (let i = 0; i < mergedData.length; i++) {
                        const item = mergedData[i];
                        const keyStr = rowAttrs.map(attr => String(item[attr] ?? '')).join('\0');
                        mergedDataLookup.set(keyStr, item);
                    }

                    const sortMeta = rowKeys.map((key) => {
                        const keyStr = key.slice(0, rowAttrs.length).join('\0');
                        
                        let orderNum = Infinity;
                        const match = mergedDataLookup.get(keyStr);
                        if (match) {
                            if (match._effectiveOrder !== undefined) {
                                orderNum = match._effectiveOrder;
                            } else {
                                let minBo = Infinity;
                                let minSo = Infinity;
                                if (match._rawItems && match._rawItems.length > 0) {
                                    for (let i = 0; i < match._rawItems.length; i++) {
                                        const raw = match._rawItems[i];
                                        const bo = Number(raw['BO No']);
                                        const so = Number(raw['SO No']);
                                        if (bo > 0 && bo < minBo) minBo = bo;
                                        if (so > 0 && so < minSo) minSo = so;
                                    }
                                } else {
                                    const bo = Number(match['BO No']);
                                    const so = Number(match['SO No']);
                                    if (bo > 0) minBo = bo;
                                    if (so > 0) minSo = so;
                                }
                                orderNum = Math.min(minBo, minSo);
                            }
                        }

                        const rowTotal = pivotData.getAggregator(key, []).value();

                        const attrValues = key.map((val) => {
                            const num = parseFloat(val);
                            const isNum = !isNaN(num) && String(num) === String(val);
                            return { val, num, isNum };
                        });

                        return {
                            key,
                            keyStr,
                            orderNum,
                            rowTotal,
                            attrValues
                        };
                    });

                    sortMeta.sort((a, b) => {
                        const totalSortDir = sortDirs['Totals'] || sortDirs['TOTAL'] || 'none';
                        if (totalSortDir !== 'none') {
                            const d = totalSortDir === 'asc' ? 1 : -1;
                            if (a.rowTotal !== b.rowTotal) {
                                return (a.rowTotal - b.rowTotal) * d;
                            }
                        }

                        let explicitSortAttrIndex = -1;
                        for (let i = 0; i < rowAttrs.length; i++) {
                            if (sortDirs[rowAttrs[i]] && sortDirs[rowAttrs[i]] !== 'none') {
                                explicitSortAttrIndex = i;
                                break;
                            }
                        }

                        const indicesToCheck = [];
                        if (explicitSortAttrIndex !== -1) {
                            indicesToCheck.push(explicitSortAttrIndex);
                        }
                        for (let i = 0; i < rowAttrs.length; i++) {
                            if (i !== explicitSortAttrIndex) {
                                indicesToCheck.push(i);
                            }
                        }

                        for (const i of indicesToCheck) {
                            const attr = rowAttrs[i];
                            const dir = sortDirs[attr] || 'none';

                            if (sortByOrderNumber && (
                                attr === 'BO No' || attr === 'SO No' ||
                                attr === 'BUYER' || attr === 'SELLER'
                            )) {
                                if (a.orderNum !== b.orderNum) {
                                    const d = dir === 'desc' ? -1 : 1;
                                    return (a.orderNum - b.orderNum) * d;
                                }
                                continue;
                            }
                            
                            const aAttr = a.attrValues[i];
                            const bAttr = b.attrValues[i];
                            if (aAttr.val !== bAttr.val) {
                                const d = dir === 'desc' ? -1 : 1;
                                if (aAttr.isNum && bAttr.isNum) {
                                    return (aAttr.num - bAttr.num) * d;
                                }
                                return String(aAttr.val).localeCompare(String(bAttr.val), undefined, { numeric: true }) * d;
                            }
                        }
                        return 0;
                    });

                    for (let i = 0; i < rowKeys.length; i++) {
                        rowKeys[i] = sortMeta[i].key;
                    }
                }
                
                this.cachedSortedRowKeys = rowKeys;
                this.cachedVisibleKeyStrings = rowKeys.map(k => k.join('\0'));
            }

            const pivotData = this.pivotDataInstance;
            const hakiCache = this.hakiCache;
            const hakaCache = this.hakaCache;
            const rowKeys = this.cachedSortedRowKeys;
            const colAttrs = pivotData.props.cols;
            const fullRowAttrs = pivotData.props.rows;
            const rowAttrs = injectedHakaHaki ? fullRowAttrs.slice(0, -1) : fullRowAttrs;
            const visibleKeys = rowKeys;

            this._rowKeysLength = rowKeys.length;
            const colKeys = pivotData.getColKeys();
            const grandTotalAggregator = pivotData.getAggregator([], []);

            let valueCellColors: any = () => { };
            let rowTotalColors: any = () => { };
            let colTotalColors: any = () => { };
            if (opts.heatmapMode) {
                const colorScaleGenerator = this.props['tableColorScaleGenerator'];
                const rowTotalValues = colKeys.map((x: any) =>
                    pivotData.getAggregator([], x).value()
                );
                rowTotalColors = colorScaleGenerator(rowTotalValues);
                const colTotalValues = rowKeys.map((x: any) =>
                    pivotData.getAggregator(x, []).value()
                );
                colTotalColors = colorScaleGenerator(colTotalValues);

                if (opts.heatmapMode === 'full') {
                    const allValues: number[] = [];
                    rowKeys.map((r: any) =>
                        colKeys.map((c: any) => allValues.push(pivotData.getAggregator(r, c).value()))
                    );
                    const colorScale = colorScaleGenerator(allValues);
                    valueCellColors = (_r: any, _c: any, v: any) => colorScale(v);
                } else if (opts.heatmapMode === 'row') {
                    const rowColorScales: any = {};
                    rowKeys.map((r: any) => {
                        const rowValues = colKeys.map((x: any) =>
                            pivotData.getAggregator(r, x).value()
                        );
                        rowColorScales[r.join('\0')] = colorScaleGenerator(rowValues);
                    });
                    valueCellColors = (r: any, _c: any, v: any) => rowColorScales[r.join('\0')](v);
                } else if (opts.heatmapMode === 'col') {
                    const colColorScales: any = {};
                    colKeys.map((c: any) => {
                        const colValues = rowKeys.map((x: any) =>
                            pivotData.getAggregator(x, c).value()
                        );
                        colColorScales[c.join('\0')] = colorScaleGenerator(colValues);
                    });
                    valueCellColors = (_r: any, c: any, v: any) => colColorScales[c.join('\0')](v);
                }
            }

            const getClickHandler =
                (this.props['tableOptions'] && this.props['tableOptions'].clickCallback)
                    ? (value: any, rowValues: any, colValues: any) => {
                        const filters: any = {};
                        for (const i of Object.keys(colAttrs || {})) {
                            const attr = colAttrs[i as any];
                            if (colValues[i] !== null) {
                                filters[attr] = colValues[i];
                            }
                        }
                        for (const i of Object.keys(rowAttrs || {})) {
                            const attr = rowAttrs[i as any];
                            if (rowValues[i] !== null) {
                                filters[attr] = rowValues[i];
                            }
                        }

                        return (e: any) =>
                            this.props['tableOptions'].clickCallback(
                                e,
                                value,
                                filters,
                                pivotData
                            );
                    }
                    : null;

            const table = React.createElement(
                'table',
                { className: 'pvtTable' },
                React.createElement(
                    'colgroup',
                    null,
                    rowAttrs.map((r: any, i: number) =>
                        React.createElement('col', {
                            key: 'col_rowAttr_' + i,
                            style: getColWidthStyle(r)
                        })
                    ),
                    colKeys.map((_colKey: string[], i: number) =>
                        React.createElement('col', {
                            key: 'col_val_' + i
                        })
                    ),
                    React.createElement('col', {
                        key: 'col_total'
                    })
                ),
                React.createElement(
                    'thead',
                    null,
                    colAttrs.map((c: any, j: number) =>
                        React.createElement(
                            'tr',
                            { key: 'colAttr' + j },
                            (j === 0 && rowAttrs.length !== 0)
                                ? rowAttrs.map((r: any, i: number) => {
                                    const sortDir = ((this.props['tableOptions'] || {}).sortDirs || {})[r] || 'none';
                                    const onSortClick = (this.props['tableOptions'] || {}).onHeaderClick;
                                    
                                    const isSortable = r === 'TIME' || r === 'Totals' || r === 'TOTAL';
                                    
                                    return React.createElement(
                                        'th',
                                        {
                                            className: `pvtAxisLabel pvtRowAxisLabel pvtCornerLabel transition-colors ${isSortable ? 'hover:bg-[#3a4252] cursor-pointer' : ''}`,
                                            key: 'rowAttr' + i,
                                            rowSpan: colAttrs.length + 1,
                                            onClick: (isSortable && onSortClick) ? () => onSortClick(r) : undefined,
                                            style: getColWidthStyle(r)
                                        },
                                        React.createElement('div', { className: 'flex items-center justify-center gap-1' },
                                            r,
                                            isSortable && React.createElement('span', { className: `text-[10px] ${sortDir === 'none' ? 'opacity-30' : 'opacity-100'}` }, 
                                                sortDir === 'asc' ? '▲' : (sortDir === 'desc' ? '▼' : '⇅')
                                            )
                                        )
                                    );
                                })
                                : null,
                            (() => {
                                const sortDir = ((this.props['tableOptions'] || {}).sortDirs || {})[c] || 'none';
                                const onSortClick = (this.props['tableOptions'] || {}).onHeaderClick;
                                
                                const isSortable = c === 'TIME' || c === 'Totals' || c === 'TOTAL';
                                
                                return React.createElement(
                                    'th',
                                    {
                                        className: `pvtAxisLabel pvtColAxisLabel transition-colors ${isSortable ? 'hover:bg-[#3a4252] cursor-pointer' : ''}`,
                                        onClick: (isSortable && onSortClick) ? () => onSortClick(c) : undefined
                                    },
                                    React.createElement('div', { className: 'flex items-center justify-center gap-1' },
                                        c,
                                        isSortable && React.createElement('span', { className: `text-[10px] ${sortDir === 'none' ? 'opacity-30' : 'opacity-100'}` }, 
                                            sortDir === 'asc' ? '▲' : (sortDir === 'desc' ? '▼' : '⇅')
                                        )
                                    )
                                );
                            })(),
                            colKeys.map((colKey, i) => {
                                const x = spanSize(colKeys, i, j);
                                if (x === -1) {
                                    return null;
                                }
                                
                                let cellContent: React.ReactNode = colKey[j];
                                let cellStyle: React.CSSProperties | undefined = undefined;

                                if (c === 'PRICE') {
                                    const prevClose = (this.props['tableOptions'] || {}).prevClose;
                                    const priceColorInfo = getPriceColorInfo(colKey[j], prevClose);
                                    if (priceColorInfo) {
                                        if (priceColorInfo.color) cellStyle = { color: priceColorInfo.color };
                                        cellContent = React.createElement(
                                            'span',
                                            { className: priceColorInfo.className, style: cellStyle },
                                            colKey[j]
                                        );
                                    }
                                } else if (c === 'BUYER' || c === 'SELLER') {
                                    const brokerColorInfo = getBrokerColorClass(String(colKey[j]).trim());
                                    if (brokerColorInfo.color) {
                                        cellStyle = { color: brokerColorInfo.color };
                                    }
                                    cellContent = React.createElement(
                                        'span',
                                        { className: brokerColorInfo.className, style: cellStyle },
                                        colKey[j]
                                    );
                                }

                                return React.createElement(
                                    'th',
                                    {
                                        className: 'pvtColLabel',
                                        key: 'colKey' + i,
                                        colSpan: x,
                                        rowSpan:
                                            j === colAttrs.length - 1 && rowAttrs.length !== 0
                                                ? 2
                                                : 1,
                                        style: cellStyle,
                                    },
                                    cellContent
                                );
                            }),
                            j === 0
                                ? (() => {
                                    const sortDir = ((this.props['tableOptions'] || {}).sortDirs || {})['Totals'] || 'none';
                                    const onSortClick = (this.props['tableOptions'] || {}).onHeaderClick;
                                    
                                    const isSortable = true; // Always sortable for Totals
                                    
                                    return React.createElement(
                                        'th',
                                        {
                                            className: 'pvtTotalLabel hover:bg-[#3a4252] cursor-pointer transition-colors',
                                            rowSpan: colAttrs.length + (rowAttrs.length === 0 ? 0 : 1),
                                            onClick: onSortClick ? () => onSortClick('Totals') : undefined
                                        },
                                        React.createElement('div', { className: 'flex items-center justify-center gap-1' },
                                            'Totals',
                                            React.createElement('span', { className: `text-[10px] ${sortDir === 'none' ? 'opacity-30' : 'opacity-100'}` }, 
                                                sortDir === 'asc' ? '▲' : (sortDir === 'desc' ? '▼' : '⇅')
                                            )
                                        )
                                    );
                                })()
                                : null
                        )
                    ),
                    (colAttrs.length === 0 && rowAttrs.length !== 0)
                        ? React.createElement(
                            'tr',
                            null,
                            rowAttrs.map((r: any, i: number) => {
                                const sortDir = ((this.props['tableOptions'] || {}).sortDirs || {})[r] || 'none';
                                const onSortClick = (this.props['tableOptions'] || {}).onHeaderClick;
                                
                                    const isSortable = r === 'TIME' || r === 'Totals' || r === 'TOTAL';
                                    
                                    return React.createElement(
                                        'th',
                                        {
                                            className: `pvtAxisLabel pvtRowAxisLabel pvtCornerLabel transition-colors ${isSortable ? 'hover:bg-[#3a4252] cursor-pointer' : ''}`,
                                            key: 'rowAttr' + i,
                                            rowSpan: 1,
                                            onClick: (isSortable && onSortClick) ? () => onSortClick(r) : undefined,
                                            style: getColWidthStyle(r)
                                        },
                                        React.createElement('div', { className: 'flex items-center justify-center gap-1' },
                                            r,
                                            isSortable && React.createElement('span', { className: `text-[10px] ${sortDir === 'none' ? 'opacity-30' : 'opacity-100'}` }, 
                                                sortDir === 'asc' ? '▲' : (sortDir === 'desc' ? '▼' : '⇅')
                                            )
                                        )
                                    );
                            }),
                            (() => {
                                const sortDir = ((this.props['tableOptions'] || {}).sortDirs || {})['Totals'] || 'none';
                                const onSortClick = (this.props['tableOptions'] || {}).onHeaderClick;

                                return React.createElement(
                                    'th',
                                    { 
                                        className: 'pvtTotalLabel hover:bg-[#3a4252] cursor-pointer transition-colors',
                                        onClick: onSortClick ? () => onSortClick('Totals') : undefined
                                    },
                                    React.createElement('div', { className: 'flex items-center justify-center gap-1' },
                                        'Totals',
                                        React.createElement('span', { className: `text-[10px] ${sortDir === 'none' ? 'opacity-30' : 'opacity-100'}` }, 
                                            sortDir === 'asc' ? '▲' : (sortDir === 'desc' ? '▼' : '⇅')
                                        )
                                    )
                                );
                            })()
                        )
                        : null
                ),
                React.createElement(
                    'tbody',
                    null,
                    (() => {
                        const visibleKeyStrings = this.cachedVisibleKeyStrings;

                        const rowSpanSize = (i_val: number, j_val: number) => {
                            const attrName = rowAttrs[j_val];
                            const isBuyerOrSeller = attrName === 'BUYER' || attrName === 'SELLER';
                            const isBoOrSo = attrName === 'BO No' || attrName === 'SO No';

                            const currRKey = visibleKeyStrings[i_val];
                            if ((this.state as any).expandedRows?.[currRKey]) return 1;

                            if (isBoOrSo) {
                                const currVal = visibleKeys[i_val][j_val];
                                if (currVal === '' || currVal === 'null' || currVal === '0') return 1;
                            }

                            if (i_val > 0) {
                                const prevRow = visibleKeys[i_val - 1];
                                const currRow = visibleKeys[i_val];
                                let samePrefix = true;
                                for (let k = 0; k <= j_val; k++) {
                                    if (prevRow[k] !== currRow[k]) {
                                        samePrefix = false;
                                        break;
                                    }
                                }
                                
                                if ((this.state as any).expandedRows?.[visibleKeyStrings[i_val - 1]]) {
                                    samePrefix = false;
                                }

                                if (samePrefix) {
                                    let sameState = true;
                                    if (isBuyerOrSeller) {
                                        if (currRow[j_val] === '' || currRow[j_val] === 'null') {
                                            sameState = false;
                                        } else {
                                            const rKeyCurr = currRKey;
                                            const rKeyPrev = visibleKeyStrings[i_val - 1];
                                            const isHakiCurr = hakiCache[rKeyCurr] || false;
                                            const isHakaCurr = hakaCache[rKeyCurr] || false;
                                            const isHakiPrev = hakiCache[rKeyPrev] || false;
                                            const isHakaPrev = hakaCache[rKeyPrev] || false;
                                            sameState = (isHakiCurr === isHakiPrev) && (isHakaCurr === isHakaPrev);
                                        }
                                    }
                                    if (sameState) return -1;
                                }
                            }

                            let len = 1;
                            const maxLimit = Math.min(visibleKeys.length, this.state.displayLimit);
                            while (i_val + len < maxLimit) {
                                const nextRKey = visibleKeyStrings[i_val + len];
                                if ((this.state as any).expandedRows?.[nextRKey]) break;

                                const currRow = visibleKeys[i_val];
                                const nextRow = visibleKeys[i_val + len];
                                if (!currRow || !nextRow) break;
                                
                                let samePrefix = true;
                                for (let k = 0; k <= j_val; k++) {
                                    if (currRow[k] !== nextRow[k]) {
                                        samePrefix = false;
                                        break;
                                    }
                                }
                                if (!samePrefix) break;

                                if (isBuyerOrSeller) {
                                    if (currRow[j_val] === '' || currRow[j_val] === 'null') {
                                        break;
                                    }
                                    const rKeyCurr = currRKey;
                                    const rKeyNext = nextRKey;
                                    const isHakiCurr = hakiCache[rKeyCurr] || false;
                                    const isHakaCurr = hakaCache[rKeyCurr] || false;
                                    const isHakiNext = hakiCache[rKeyNext] || false;
                                    const isHakaNext = hakaCache[rKeyNext] || false;
                                    if (isHakiCurr !== isHakiNext || isHakaCurr !== isHakaNext) break;
                                }
                                len++;
                            }
                            return len;
                        };

                        return visibleKeys.slice(0, this.state.displayLimit).map((rowKey, i) => {
                            const totalAggregator = pivotData.getAggregator(rowKey, []);

                            const rKeyStr = visibleKeyStrings[i];
                            const isExpanded = !!(this.state as any).expandedRows?.[rKeyStr];
                            let hasChildrenToExpand = false;
                            const mergedChildren = this.props['tableOptions']?.getMergedChildren?.(rowKey, rowAttrs);
                            if (mergedChildren && mergedChildren.length > 1) {
                                hasChildrenToExpand = true;
                            }

                            const mainTrChildren = rowKey.map((txt, j) => {
                                    if (j >= rowAttrs.length) return null;
                                    const attrName = rowAttrs[j];
                                    const isBuyer = attrName === 'BUYER';
                                    const isSeller = attrName === 'SELLER';
                                    const isPrice = attrName === 'PRICE';
                                    const rKey = rKeyStr;

                                const x = rowSpanSize(i, j);
                                if (x === -1) {
                                    return null;
                                }

                                const isHakiCell = isBuyer && hakiCache[rKey];
                                const isHakaCell = isSeller && hakaCache[rKey];
                                const extraClass = (isHakiCell || isHakaCell) ? ' bg-haka-haki' : '';

                                let cellContent: React.ReactNode = txt;
                                let customStyle: React.CSSProperties = {
                                    verticalAlign: 'top',
                                    ...getColWidthStyle(attrName)
                                };

                                if (isBuyer || isSeller) {
                                    const brokerColorInfo = getBrokerColorClass(String(txt));
                                    if (brokerColorInfo.color) {
                                        customStyle.color = brokerColorInfo.color;
                                    }

                                    const brokerSpan = React.createElement(
                                        'span',
                                        { className: brokerColorInfo.className, style: { color: customStyle.color } },
                                        txt
                                    );

                                    let shouldShowExpand = hasChildrenToExpand;
                                    if (hasChildrenToExpand) {
                                        const boNoIdx = rowAttrs.indexOf('BO No');
                                        const soNoIdx = rowAttrs.indexOf('SO No');
                                        const boVal = boNoIdx >= 0 ? String(rowKey[boNoIdx] ?? '') : '';
                                        const soVal = soNoIdx >= 0 ? String(rowKey[soNoIdx] ?? '') : '';

                                        if (boNoIdx >= 0 || soNoIdx >= 0) {
                                            if (isBuyer) {
                                                shouldShowExpand = soVal !== '' && soVal !== '0' && soVal !== 'null';
                                            } else if (isSeller) {
                                                shouldShowExpand = boVal !== '' && boVal !== '0' && boVal !== 'null';
                                            }
                                        }
                                    }

                                    const expandBtn = shouldShowExpand
                                        ? React.createElement('button', {
                                            onClick: (e: any) => { e.stopPropagation(); this.toggleRow(rKey); },
                                            className: 'w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center bg-[#2a3348] hover:bg-[#3a4252] rounded text-[10px] text-white cursor-pointer transition-colors shadow-sm'
                                          }, isExpanded ? '-' : '+')
                                        : null;

                                    const isGroupActive = !!(this.props['tableOptions'] || {}).groupActive;

                                    // Always wrap in a flex div so alignment beats text-align:center !important from CSS
                                    if (!isGroupActive) {
                                        // Group: OFF → both BUYER and SELLER centered
                                        cellContent = React.createElement('div', {
                                            className: 'flex items-center justify-center gap-1 w-full'
                                        }, expandBtn, brokerSpan);
                                    } else if (isBuyer) {
                                        // Group: ON, BUYER → right-aligned: [+/-] [CODE]
                                        cellContent = React.createElement('div', {
                                            className: 'flex items-center justify-end gap-1 w-full'
                                        }, expandBtn, brokerSpan);
                                    } else {
                                        // Group: ON, SELLER → left-aligned: [CODE] [+/-]
                                        cellContent = React.createElement('div', {
                                            className: 'flex items-center justify-start gap-1 w-full'
                                        }, brokerSpan, expandBtn);
                                    }
                                } else if (isPrice) {
                                    const prevClose = (this.props['tableOptions'] || {}).prevClose;
                                    const priceColorInfo = getPriceColorInfo(txt, prevClose);
                                    if (priceColorInfo) {
                                        if (priceColorInfo.color) customStyle.color = priceColorInfo.color;
                                        cellContent = React.createElement(
                                            'span',
                                            { className: priceColorInfo.className, style: { color: customStyle.color } },
                                            txt
                                        );
                                    }
                                }



                                return React.createElement(
                                    'th',
                                    {
                                        key: 'rowKeyLabel' + i + '-' + j,
                                        className: 'pvtRowLabel' + extraClass,
                                        rowSpan: x,
                                        colSpan: 1,
                                        style: customStyle,
                                    },
                                    cellContent
                                );
                            });

                        const mainTr = React.createElement(
                            'tr',
                            { key: 'rowKeyRow' + i },
                            mainTrChildren,
                            colKeys.map((colKey, j) => {
                                const aggregator = pivotData.getAggregator(rowKey, colKey);
                                let customValStyle = valueCellColors(rowKey, colKey, aggregator.value()) || {};
                                let cellClass = 'pvtVal';

                                const priceIndex = rowAttrs.indexOf('PRICE');
                                if (priceIndex !== -1 && rowKey[priceIndex] !== undefined) {
                                    const prevClose = (this.props['tableOptions'] || {}).prevClose;
                                    const priceColorInfo = getPriceColorInfo(rowKey[priceIndex], prevClose);
                                    if (priceColorInfo && priceColorInfo.color) {
                                        customValStyle = { ...customValStyle, color: priceColorInfo.color };
                                        if (priceColorInfo.className) cellClass += ' ' + priceColorInfo.className;
                                    }
                                }

                                return React.createElement(
                                    'td',
                                    {
                                        className: cellClass,
                                        key: 'pvtVal' + i + '-' + j,
                                        onClick:
                                            (getClickHandler &&
                                                getClickHandler(aggregator.value(), rowKey, colKey)) || undefined,
                                        style: customValStyle,
                                    },
                                    aggregator.format(aggregator.value())
                                );
                            }),
                            (() => {
                                let totalStyle = colTotalColors(totalAggregator.value()) || {};
                                let cellClass = 'pvtTotal';

                                const priceIndex = rowAttrs.indexOf('PRICE');
                                if (priceIndex !== -1 && rowKey[priceIndex] !== undefined) {
                                    const prevClose = (this.props['tableOptions'] || {}).prevClose;
                                    const priceColorInfo = getPriceColorInfo(rowKey[priceIndex], prevClose);
                                    if (priceColorInfo && priceColorInfo.color) {
                                        totalStyle = { ...totalStyle, color: priceColorInfo.color };
                                        if (priceColorInfo.className) cellClass += ' ' + priceColorInfo.className;
                                    }
                                }

                                return React.createElement(
                                    'td',
                                    {
                                        className: cellClass,
                                        onClick:
                                            (getClickHandler &&
                                                getClickHandler(totalAggregator.value(), rowKey, [null])) || undefined,
                                        style: totalStyle,
                                    },
                                    totalAggregator.format(totalAggregator.value())
                                );
                            })()
                        );

                        let childrenRows: any[] = [];
                        if (isExpanded && hasChildrenToExpand) {
                            const prevCloseForChild = (this.props['tableOptions'] || {}).prevClose;
                            childrenRows = mergedChildren.map((childItem: any, childIdx: number) => {
                                // Determine price color for this child row (used on price cell + total)
                                const childPrice = childItem['PRICE'];
                                const childPriceInfo = getPriceColorInfo(childPrice, prevCloseForChild);
                                const childPriceColor = childPriceInfo?.color;

                                return React.createElement('tr', { key: 'rowKeyRow' + i + '_child' + childIdx, className: 'pvtChildRow bg-[#1a2035] opacity-90 shadow-inner' },
                                    rowAttrs.map((attrName: string, j: number) => {
                                        const v = String(childItem[attrName] ?? '');
                                        const isBuyer = attrName === 'BUYER';
                                        const isSeller = attrName === 'SELLER';
                                        const isPrice = attrName === 'PRICE';
                                        const isHakiChild = childItem['HAKA HAKI'] === 'HAKI';
                                        const isHakaChild = childItem['HAKA HAKI'] === 'HAKA';
                                        const isHakiCell = isBuyer && isHakiChild;
                                        const isHakaCell = isSeller && isHakaChild;

                                        let cellStyle: React.CSSProperties = {
                                            ...getColWidthStyle(attrName)
                                        };
                                        let cellContent: React.ReactNode = v;

                                        if (isBuyer || isSeller) {
                                            // Apply broker color
                                            const brokerInfo = getBrokerColorClass(v);
                                            const bgClass = (isHakiCell || isHakaCell) ? ' bg-haka-haki' : '';
                                            if (brokerInfo.color) cellStyle.color = brokerInfo.color;
                                            cellContent = React.createElement('span',
                                                { className: brokerInfo.className, style: { color: brokerInfo.color } },
                                                v
                                            );
                                            return React.createElement('td', {
                                                key: 'child_th_' + j,
                                                className: 'pvtRowLabel text-center border-[#3a4252]/40' + bgClass,
                                                style: cellStyle
                                            }, cellContent);
                                        }

                                        if (isPrice && childPriceColor) {
                                            // Apply price color
                                            cellStyle.color = childPriceColor;
                                            cellContent = React.createElement('span',
                                                { className: 'font-bold', style: { color: childPriceColor } },
                                                v
                                            );
                                        }

                                        return React.createElement('td', {
                                            key: 'child_th_' + j,
                                            className: 'pvtRowLabel text-center border-[#3a4252]/40',
                                            style: cellStyle
                                        }, cellContent);
                                    }),
                                    colKeys.map((_colKey: string[], j: number) => {
                                        return React.createElement('td', { key: 'child_td_' + j, className: 'pvtVal text-center border-[#3a4252]/40' }, '');
                                    }),
                                    // Total volume cell — colored by price
                                    React.createElement('td', {
                                        className: 'pvtTotal text-right font-mono pr-2 border-[#3a4252]/40',
                                        style: childPriceColor ? { color: childPriceColor, fontWeight: 'bold' } : { color: '#4c566a' }
                                    },
                                        childItem['VOLUME'] ? childItem['VOLUME'].toLocaleString() : ''
                                    )
                                );
                            });
                        }
                        return React.createElement(React.Fragment, { key: 'frag' + i }, mainTr, ...childrenRows);
                    });
                })(),
                    React.createElement(
                        'tr',
                        { className: 'pvtTotalRow' },
                        (() => {
                            const sortDir = ((this.props['tableOptions'] || {}).sortDirs || {})['Totals_Cols'] || 'none';
                            const onSortClick = (this.props['tableOptions'] || {}).onHeaderClick;

                            return React.createElement(
                                'th',
                                {
                                    className: 'pvtTotalLabel hover:bg-[#3a4252] cursor-pointer transition-colors',
                                    colSpan: rowAttrs.length,
                                    onClick: onSortClick ? () => onSortClick('Totals_Cols') : undefined
                                },
                                React.createElement('div', { className: 'flex items-center justify-center gap-1' },
                                    'Totals',
                                    React.createElement('span', { className: `text-[10px] ${sortDir === 'none' ? 'opacity-30' : 'opacity-100'}` }, 
                                        sortDir === 'asc' ? '◀' : (sortDir === 'desc' ? '▶' : '⇆')
                                    )
                                )
                            );
                        })(),
                        colKeys.map((colKey, i) => {
                            const totalAggregator = pivotData.getAggregator([], colKey);
                            return React.createElement(
                                'td',
                                {
                                    className: 'pvtTotal',
                                    key: 'total' + i,
                                    onClick:
                                        (getClickHandler &&
                                            getClickHandler(totalAggregator.value(), [null], colKey)) || undefined,
                                    style: rowTotalColors(totalAggregator.value()),
                                },
                                totalAggregator.format(totalAggregator.value())
                            );
                        }),
                        React.createElement(
                            'td',
                            {
                                onClick:
                                    (getClickHandler &&
                                        getClickHandler(
                                            grandTotalAggregator.value(),
                                            [null],
                                            [null]
                                        )) || undefined,
                                className: 'pvtGrandTotal',
                            },
                            grandTotalAggregator.format(grandTotalAggregator.value())
                        )
                    ),
                    React.createElement(
                        'tr',
                        { className: 'pvtDummyRow' },
                        React.createElement('td', {
                            colSpan:
                                rowAttrs.length +
                                colKeys.length +
                                (colAttrs.length === 0 ? 0 : 1) + 1,
                        })
                    )
                )
            );

            return React.createElement(
                'div',
                {
                    className: 'pvtTableContainer',
                    key: 'pvtTableContainer',
                    ref: this.containerRef,
                    style: {
                        position: 'relative',
                        width: '100%',
                        minHeight: '20px'
                    }
                },
                table,
                this.state.displayLimit < visibleKeys.length && React.createElement('div', {
                    style: {
                        height: '40px',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#888',
                        fontSize: '11px',
                        fontStyle: 'italic',
                        marginTop: '10px'
                    }
                }, `Loading more rows... (${Math.min(this.state.displayLimit, visibleKeys.length)} / ${visibleKeys.length})`)
            );
        }
    }

    (TableRenderer as any).defaultProps = Object.assign({}, PivotData.defaultProps, {
        tableColorScaleGenerator: redColorScaleGenerator,
        tableOptions: {},
    });
    (TableRenderer as any).propTypes = Object.assign({}, PivotData.propTypes, {
        tableColorScaleGenerator: PropTypes.func,
        tableOptions: PropTypes.object,
    });

    return TableRenderer as any as React.ComponentType<any>;
}

class TSVExportRenderer extends React.PureComponent<any> {
    static defaultProps = PivotData.defaultProps;
    static propTypes = PivotData.propTypes;

    override render() {
        const pivotData = new PivotData(this.props);
        const rowKeys = pivotData.getRowKeys();
        const colKeys = pivotData.getColKeys();
        if (rowKeys.length === 0) {
            rowKeys.push([]);
        }
        if (colKeys.length === 0) {
            colKeys.push([]);
        }

        const headerRow = pivotData.props.rows.map((r: any) => r);
        if (colKeys.length === 1 && colKeys[0] && colKeys[0].length === 0) {
            headerRow.push(this.props['aggregatorName']);
        } else {
            colKeys.map(c => headerRow.push(c.join('-')));
        }

        const result = rowKeys.map(r => {
            const row = r.map(x => x);
            colKeys.map(c => {
                const v = pivotData.getAggregator(r, c).value();
                row.push(v ? v : '');
            });
            return row;
        });

        result.unshift(headerRow);

        return React.createElement('textarea', {
            value: result.map(r => r.join('\t')).join('\n'),
            style: { width: window.innerWidth / 2, height: window.innerHeight / 2 },
            readOnly: true,
        });
    }
}

export default {
    Table: makeRenderer(),
    'Table Heatmap': makeRenderer({ heatmapMode: 'full' }),
    'Table Col Heatmap': makeRenderer({ heatmapMode: 'col' }),
    'Table Row Heatmap': makeRenderer({ heatmapMode: 'row' }),
    'Exportable TSV': TSVExportRenderer as any as React.ComponentType<any>,
};
